package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.internal.concurrent.ThreadLocalRef
import kotlin.jvm.JvmStatic

/**
 * 当前渲染上下文中的字体层级（类似 [ThemeAccess]）。
 *
 * 线程局部（[ThreadLocalRef]）存储三层字体：全局 / 页面 / 元素层。各渲染器通过 [resolve] 得到最终合并后的 FontStyle。
 *
 * 元素层：由 UIRenderer 在递归渲染每个元素前后 [pushElement] / [popElement]，把元素自身字体压成
 * 环境量。维护单个「累积合并」的 elementFont（内层 merge 外层），因此对子孙级联；无字体的 4 参
 * drawText 也能经 `resolve(null)` 拿到「元素 > 祖先 > 页面 > 全局」的最终字体。
 *
 * 线程隔离：与 [ThemeAccess] 相同策略，fabric 渲染线程单线程。
 */
object FontAccess {

    private val current: ThreadLocalRef<Context> = ThreadLocalRef { Context() }

    /** 设置全局默认字体。 */
    @JvmStatic
    fun setGlobal(font: FontStyle?) {
        current.get().globalFont = font
    }

    /** 设置页面级字体（由 UIRenderer 在渲染入口压栈）。 */
    @JvmStatic
    fun setPage(font: FontStyle?) {
        current.get().pageFont = font
    }

    /** 获取当前页面级字体（可能为 null）。 */
    @JvmStatic
    fun page(): FontStyle? = current.get().pageFont

    /** 获取当前全局字体（可能为 null）。 */
    @JvmStatic
    fun global(): FontStyle? = current.get().globalFont

    /**
     * 压入一层元素字体（渲染递归进入元素时调用）。
     *
     * 用「内层 merge 外层」累积合并：返回旧的 elementFont 供 [popElement] 还原。
     * font 为 null 表示该元素不声明字体，elementFont 保持不变（仍继承祖先层）。
     *
     * @param font 该元素声明的字体，可为 null
     * @return 调用前的 elementFont（须原样传给 popElement 还原）
     */
    @JvmStatic
    fun pushElement(font: FontStyle?): FontStyle? {
        val ctx = current.get()
        val prev = ctx.elementFont
        if (font != null) {
            // 内层覆盖外层：font 的非 null 字段优先，缺省继承已累积的祖先层
            ctx.elementFont = mergeLayer(font, prev)
        }
        return prev
    }

    /** 还原元素层到 push 之前（须传入对应 pushElement 的返回值）。 */
    @JvmStatic
    fun popElement(prev: FontStyle?) {
        current.get().elementFont = prev
    }

    /** 获取当前累积的元素层字体（可能为 null）。 */
    @JvmStatic
    fun element(): FontStyle? = current.get().elementFont

    /** 重置为初始状态（测试用）。 */
    @JvmStatic
    fun reset() {
        val ctx = current.get()
        ctx.globalFont = null
        ctx.pageFont = null
        ctx.elementFont = null
    }

    /**
     * 四层合并：元素显式参数 > 累积元素层（自身+祖先） > 页面级 > 全局级。
     *
     * 每层的非 null 字段覆盖下层；最终 null 字段取 FontStyle 硬编码默认值。无字体的 4 参 drawText
     * 传 null 即可拿到「累积元素层 > 页面 > 全局」。
     *
     * @param elementFont 调用方额外的元素级字体声明，可为 null（通常传 null，靠 push 的环境层）
     * @return 合并后的最终 FontStyle（所有 effective* 方法保证非 null 返回）
     */
    @JvmStatic
    fun resolve(elementFont: FontStyle?): FontStyle {
        val ctx = current.get()
        // 从底层往上 merge：global → page → 累积元素层 → 传入 elementFont
        var result: FontStyle? = ctx.globalFont
        result = mergeLayer(ctx.pageFont, result)
        result = mergeLayer(ctx.elementFont, result)
        result = mergeLayer(elementFont, result)
        // result 可能仍为 null（所有层都未设置），返回 empty 让 effective* 走默认
        return result ?: FontStyle.empty()
    }

    /** 把上层 layer 合并到下层 below 之上：任一为 null 取另一个，否则 layer 非 null 字段覆盖 below。 */
    private fun mergeLayer(layer: FontStyle?, below: FontStyle?): FontStyle? = when {
        layer == null -> below
        below == null -> layer
        else -> layer.merge(below)
    }

    /**
     * 解析最终字体名：元素/页面/全局任一层显式声明了字体名则用之；
     * 否则（[FontStyle.effectiveName] 回落到 [FontStyle.DEFAULT_NAME]）改用主题级 `fontFamily`。
     * 默认主题 fontFamily 同为 minecraft:default，故非自定义字体主题零回归。
     * TextRenderer / ButtonRenderer 等带文字控件渲染器共用本助手。
     */
    @JvmStatic
    fun resolveName(font: FontStyle, theme: ControlTheme): String {
        val name = font.effectiveName()
        return if (FontStyle.DEFAULT_NAME == name) theme.fontFamily else name
    }

    /** 内部上下文容器。 */
    private class Context {
        var globalFont: FontStyle? = null
        var pageFont: FontStyle? = null

        /** 累积的元素层字体（自身与祖先 merge 后的单值），随渲染递归 push/pop。 */
        var elementFont: FontStyle? = null
    }
}
